/**
 * CrUX Record API Client (Story IMC-27, Task 1)
 *
 * Fetches current CrUX data for a URL/origin using the dedicated CrUX API
 * endpoint `chromeuxreport.googleapis.com/v1/records:queryRecord`.
 *
 * Unlike PSI (which includes *some* CrUX data), this endpoint exposes:
 *   - LCP image subparts (TTFB, load delay, load duration, render delay)   [Feb 2025+]
 *   - LCP resource_type (fraction breakdown: image/text/video)              [Feb 2025+]
 *   - round_trip_time (p75 network RTT)                                     [Feb 2025+]
 *   - navigation_types (fraction breakdown of page navigation types)         [Feb 2025+]
 *
 * Auth: API key (same key as PageSpeed Insights — free tier, ~150 req/min).
 * Form factor: PHONE | DESKTOP | TABLET (explicit; omit to aggregate).
 *
 * Origins/URLs with insufficient CrUX traffic return HTTP 404. We treat this
 * as "no data" (adapterOk([])), not an error, so the adapter can still return
 * Lighthouse metrics for small sites.
 *
 * ADR-013: Plain function adapter pattern
 * ADR-014: Defensive API versioning
 */

import type { AdapterResult } from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

const CRUX_RECORD_BASE =
  "https://chromeuxreport.googleapis.com/v1/records:queryRecord";

/** Timeout: 8s per call (runs in parallel with PSI + CrUX History) */
const CRUX_RECORD_TIMEOUT_MS = 8_000;

/** Form factors queried (mapped to our lowercased `form_factor` dimension values) */
export const CRUX_FORM_FACTORS = ["PHONE", "DESKTOP", "TABLET"] as const;
export type CrUXFormFactor = (typeof CRUX_FORM_FACTORS)[number];

/** Lowercase form-factor values used in record data + downstream dimension filters */
export type CrUXFormFactorLowercase = Lowercase<CrUXFormFactor>;

/**
 * Type-safe conversion from the upper-case CrUX API form factor to the
 * lowercase dimension value we expose in records. Using an explicit function
 * (not a string literal cast) guarantees at compile time that any future
 * form-factor addition requires updating both the API constant and the type.
 */
function toFormFactorLowercase(ff: CrUXFormFactor): CrUXFormFactorLowercase {
  // TypeScript's template-literal type resolver narrows Lowercase<"PHONE">
  // to "phone" etc., so this cast is safe by construction.
  return ff.toLowerCase() as CrUXFormFactorLowercase;
}

/**
 * Metrics requested from CrUX queryRecord. Core Web Vitals already come from
 * PSI's loadingExperience, so this client focuses on the subparts/navigation
 * data ONLY available via the dedicated CrUX endpoint.
 */
const CRUX_RECORD_METRICS = [
  "largest_contentful_paint_image_time_to_first_byte",
  "largest_contentful_paint_image_resource_load_delay",
  "largest_contentful_paint_image_resource_load_duration",
  "largest_contentful_paint_image_element_render_delay",
  "largest_contentful_paint_resource_type",
  "round_trip_time",
  "navigation_types",
] as const;

/** Map CrUX API metric names → our record field names (for p75 numeric metrics) */
const P75_METRIC_KEYS: Record<string, string> = {
  largest_contentful_paint_image_time_to_first_byte: "lcp_image_ttfb",
  largest_contentful_paint_image_resource_load_delay: "lcp_image_load_delay",
  largest_contentful_paint_image_resource_load_duration: "lcp_image_load_duration",
  largest_contentful_paint_image_element_render_delay: "lcp_image_render_delay",
  round_trip_time: "round_trip_time",
};

// ============================================================================
// Response Types
// ============================================================================

interface CrUXPercentiles {
  p75?: number | string | null;
}

interface CrUXFractions {
  [label: string]: number;
}

interface CrUXMetric {
  percentiles?: CrUXPercentiles;
  fractions?: CrUXFractions;
}

interface CrUXRecordResponse {
  record?: {
    key?: { url?: string; origin?: string; formFactor?: string };
    metrics?: Record<string, CrUXMetric>;
  };
}

// ============================================================================
// Public API
// ============================================================================

export interface CrUXRecordData {
  _reportType: "cruxRecord";
  /** Lowercase derivative of the CrUX form-factor constant. */
  form_factor: CrUXFormFactorLowercase;
  /** p75 LCP image TTFB in ms (null if metric missing) */
  lcp_image_ttfb: number | null;
  lcp_image_load_delay: number | null;
  lcp_image_load_duration: number | null;
  lcp_image_render_delay: number | null;
  /** p75 round-trip-time in ms (null if missing) */
  round_trip_time: number | null;
  /** Dominant LCP resource type (image|text|video|other|null) */
  lcp_resource_type: string | null;
  /** Fraction breakdown of LCP resource types (labels vary) */
  lcp_resource_type_fractions: Record<string, number> | null;
  /** Dominant navigation type */
  navigation_type: string | null;
  /** Fraction breakdown of navigation types */
  navigation_type_fractions: Record<string, number> | null;
  /**
   * Index signature so adapters can push these records into the standard
   * `Record<string, unknown>[]` shape without unsafe `as unknown as` casts.
   */
  [key: string]: unknown;
}

/**
 * Safely coerce a CrUX percentile value to a finite number.
 * The API sometimes returns strings (e.g. "1200") for histogram buckets; we
 * normalize to number, returning null for missing/non-finite values.
 */
function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Pick the dominant label from a fraction map (highest fraction).
 * Returns null if empty or invalid.
 */
function dominantLabel(fractions: CrUXFractions | undefined): string | null {
  if (!fractions) return null;
  let best: string | null = null;
  let bestVal = -Infinity;
  for (const [label, value] of Object.entries(fractions)) {
    if (typeof value === "number" && Number.isFinite(value) && value > bestVal) {
      bestVal = value;
      best = label;
    }
  }
  return best;
}

/**
 * Normalize fraction values to finite numbers; drop invalid entries.
 */
function sanitizeFractions(
  fractions: CrUXFractions | undefined
): Record<string, number> | null {
  if (!fractions) return null;
  const out: Record<string, number> = {};
  for (const [label, value] of Object.entries(fractions)) {
    if (typeof value === "number" && Number.isFinite(value)) {
      out[label] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Fetch CrUX Record data for a URL at the given form factor.
 *
 * Returns a single CrUXRecordData record on success.
 * Returns adapterOk(null) when the origin/URL has no CrUX data (HTTP 404 OR
 * absent metrics) — this is expected for small sites and should NOT fail the
 * adapter.
 */
export async function fetchCruxRecord(
  targetUrl: string,
  apiKey: string,
  formFactor: CrUXFormFactor
): Promise<AdapterResult<CrUXRecordData | null>> {
  const body = {
    url: targetUrl,
    formFactor,
    metrics: CRUX_RECORD_METRICS,
  };

  const apiUrl = new URL(CRUX_RECORD_BASE);
  apiUrl.searchParams.set("key", apiKey);

  const result = await safeFetch(
    apiUrl.toString(),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    CRUX_RECORD_TIMEOUT_MS
  );

  if (!result.success) {
    // 404 = origin/URL has insufficient CrUX data → treat as "no data",
    // not an error. This is expected for small sites (<1000 monthly users).
    // safeFetch maps any non-ok HTTP to AdapterError with providerStatus preserved.
    if (result.error.providerStatus === 404) {
      return adapterOk(null);
    }
    return adapterErr({
      ...result.error,
      message: sanitizeErrorMessage(result.error.message),
    });
  }

  let data: CrUXRecordResponse;
  try {
    data = (await result.data.json()) as CrUXRecordResponse;
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: sanitizeErrorMessage(
        `Failed to parse CrUX Record response: ${err instanceof Error ? err.message : String(err)}`
      ),
      retryable: false,
    });
  }

  const metrics = data.record?.metrics;
  if (!metrics || Object.keys(metrics).length === 0) {
    return adapterOk(null);
  }

  const record: CrUXRecordData = {
    _reportType: "cruxRecord",
    form_factor: toFormFactorLowercase(formFactor),
    lcp_image_ttfb: null,
    lcp_image_load_delay: null,
    lcp_image_load_duration: null,
    lcp_image_render_delay: null,
    round_trip_time: null,
    lcp_resource_type: null,
    lcp_resource_type_fractions: null,
    navigation_type: null,
    navigation_type_fractions: null,
  };

  // Extract p75 metrics — record now has an index signature so direct
  // assignment type-checks without casts.
  for (const [apiKey, recordKey] of Object.entries(P75_METRIC_KEYS)) {
    const p75 = metrics[apiKey]?.percentiles?.p75;
    record[recordKey] = toFiniteNumber(p75);
  }

  // LCP resource type (fraction breakdown)
  const lcpTypeMetric = metrics.largest_contentful_paint_resource_type;
  record.lcp_resource_type = dominantLabel(lcpTypeMetric?.fractions);
  record.lcp_resource_type_fractions = sanitizeFractions(lcpTypeMetric?.fractions);

  // Navigation types (fraction breakdown)
  const navMetric = metrics.navigation_types;
  record.navigation_type = dominantLabel(navMetric?.fractions);
  record.navigation_type_fractions = sanitizeFractions(navMetric?.fractions);

  return adapterOk(record);
}
